/**
 * The printer-independent document model (docs/architecture/printing.md).
 * Templates produce a list of blocks; the encoder turns blocks into ESC/POS
 * text commands, a rasteriser (platform code) into an image, and the apps
 * into an on-screen preview.
 *
 * Moved from the proof of concept (`web/shared/doc.ts`).
 */

export type Align = "left" | "center" | "right";
export type Font = "A" | "B";
export type PaperWidth = 58 | 80;

export type TextStyle = {
	align?: Align;
	bold?: boolean;
	underline?: boolean;
	invert?: boolean;
	/** Character width multiplier, 1 to 8. */
	w?: number;
	/** Character height multiplier, 1 to 8. */
	h?: number;
	font?: Font;
};

export type Column = {
	text: string;
	/** Relative width of this column. */
	weight: number;
	align?: Align;
};

/** 1 bit per pixel, row-major, most significant bit first, rows padded to whole bytes. 1 is black. */
export type MonoBitmap = {
	width: number;
	height: number;
	data: Uint8Array;
};

export type Block =
	| { t: "text"; text: string; style?: TextStyle }
	| { t: "row"; cols: Column[]; style?: TextStyle }
	| { t: "rule"; ch?: string }
	| { t: "feed"; lines: number }
	| { t: "qr"; data: string; size?: number; align?: Align }
	| { t: "barcode"; data: string; height?: number; align?: Align }
	| { t: "bitmap"; bitmap: MonoBitmap; align?: Align }
	/**
	 * The restaurant's logo (PR-09, P5-T27), by its stored file. The printing
	 * device swaps it for a `bitmap` dithered for the paper (`withLogos`), or
	 * leaves it out when it has no picture of it; layout skips any left over.
	 */
	| { t: "logo"; fileId: string; align?: Align }
	| { t: "cut"; full?: boolean }
	| { t: "beep"; times?: number }
	| { t: "drawer" }
	/**
	 * Different blocks for 58 mm and 80 mm paper, such as a rate column that
	 * fits only on 80 mm. Keeps a stored document independent of the paper.
	 */
	| { t: "byWidth"; w58: Block[]; w80: Block[] };

/** What a printer can do besides printing: `printers.capabilities`. */
export type PrinterCapabilities = {
	cut: boolean;
	drawer: boolean;
	beep: boolean;
};

/** Replaces each `byWidth` block with the blocks for the paper. */
export function forPaper(blocks: Block[], paper: PaperWidth): Block[] {
	return blocks.flatMap((b) =>
		b.t === "byWidth" ? forPaper(paper === 58 ? b.w58 : b.w80, paper) : [b],
	);
}

/**
 * The blocks one printer prints: `byWidth` resolved for its paper, a cut
 * turned into a feed when it has no cutter, and beeps and drawer pulses
 * dropped when it has no buzzer or drawer. Text mode and image mode both
 * start from this.
 */
export function forPrinter(
	blocks: Block[],
	printer: { paper: PaperWidth; capabilities: PrinterCapabilities },
): Block[] {
	const { capabilities } = printer;
	return forPaper(blocks, printer.paper).flatMap((b): Block[] => {
		if (b.t === "cut" && !capabilities.cut) return [{ t: "feed", lines: 4 }];
		if (b.t === "beep" && !capabilities.beep) return [];
		if (b.t === "drawer" && !capabilities.drawer) return [];
		return [b];
	});
}

/**
 * The logo's largest size on the paper, in dots: 60% of the width (whole
 * bytes, as rasters are byte aligned) and 40% of it high, so a tall logo does
 * not waste paper.
 */
export function logoBox(paper: PaperWidth): { maxWidth: number; maxHeight: number } {
	const dots = PAPER[paper].dots;
	return { maxWidth: Math.floor((dots * 0.6) / 8) * 8, maxHeight: Math.round(dots * 0.4) };
}

/** Every logo file a document shows, both widths included. */
export function logoFiles(blocks: Block[]): string[] {
	const found = new Set<string>();
	const walk = (list: Block[]) => {
		for (const b of list) {
			if (b.t === "logo") found.add(b.fileId);
			else if (b.t === "byWidth") {
				walk(b.w58);
				walk(b.w80);
			}
		}
	};
	walk(blocks);
	return [...found];
}

/**
 * Replaces each `logo` block with its bitmap for the paper, or drops it when
 * `bitmapOf` has none (not cached, failed to load): the bill still prints,
 * with the restaurant's name under where the logo would be.
 */
export function withLogos(
	blocks: Block[],
	paper: PaperWidth,
	bitmapOf: (fileId: string) => MonoBitmap | null,
): Block[] {
	return forPaper(blocks, paper).flatMap((b): Block[] => {
		if (b.t !== "logo") return [b];
		const bitmap = bitmapOf(b.fileId);
		return bitmap
			? [
					{ t: "bitmap", bitmap, align: b.align ?? "center" },
					{ t: "feed", lines: 1 },
				]
			: [];
	});
}

export const PAPER: Record<PaperWidth, { dots: number; colsA: number; colsB: number }> = {
	80: { dots: 576, colsA: 48, colsB: 64 },
	58: { dots: 384, colsA: 32, colsB: 42 },
};

/** Characters per line for a font and width multiplier. */
export function charsPerLine(paper: PaperWidth, font: Font = "A", w = 1): number {
	const p = PAPER[paper];
	return Math.floor((font === "B" ? p.colsB : p.colsA) / Math.max(1, w));
}
